import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Pencil, LogOut, ChevronRight, Camera, Info, Users, CalendarHeart, Images, UserPlus, UserCheck, UserX, Settings as SettingsIcon, ShieldCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { pickAndUpload } from "@/lib/upload";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { longDate } from "@/lib/date";
import { Avatar, Field, Input, PrimaryButton, GhostButton, Sheet, Spinner } from "@/components/peeps/ui";
import { TabLayout } from "@/components/peeps/TabLayout";
import { HomeButton } from "@/components/peeps/HomeButton";
import { ProximitySettings } from "@/components/peeps/ProximitySettings";
import { PushToggle } from "@/components/peeps/PushToggle";
import { GoogleAccounts } from "@/components/peeps/GoogleAccounts";
import { PlanImport } from "@/components/peeps/PlanImport";
import { PartifulConnect } from "@/components/peeps/PartifulConnect";

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center flex-1">
      <span className="font-display font-bold text-[20px]" style={{ color: colors.onSurface }}>{value}</span>
      <span className="font-display text-[11.5px]" style={{ color: colors.faint }}>{label}</span>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center gap-1.5 mb-2.5 mt-7">
      <Icon size={14} color={colors.brandSecondary} />
      <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>{title}</p>
      {right}
    </div>
  );
}

function EventRow({ ev, onOpen }) {
  const photo = ev.photo_url ? mediaUrl(ev.photo_url) : null;
  return (
    <button data-testid={`profile-event-${ev.id}`} onClick={onOpen} className="flex items-center gap-3 rounded-xl p-2.5 w-full text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 flex items-center justify-center" style={{ background: colors.surfaceTertiary }}>
        {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <CalendarHeart size={18} color={colors.muted} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-[13.5px] truncate" style={{ color: colors.onSurface }}>{ev.title}</p>
        <p className="font-display text-[11.5px] truncate" style={{ color: colors.faint }}>{longDate(ev.date)}{ev.time ? ` · ${ev.time}` : ""} · {ev.group_name}</p>
      </div>
      <ChevronRight size={16} color={colors.muted} />
    </button>
  );
}

export default function Profile() {
  const { user, setUser, signOut } = useAuth();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [name, setName] = useState(user?.display_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [photo, setPhoto] = useState(user?.profile_photo_url || null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [photos, setPhotos] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSocial = useCallback(async () => {
    const [f, r, e, p, fd] = await Promise.all([
      api.listFriends().catch(() => []),
      api.friendRequests().catch(() => ({ incoming: [] })),
      api.myEvents().catch(() => ({ upcoming: [], past: [] })),
      api.myPhotos().catch(() => []),
      api.friendsFeed().catch(() => ({ items: [] })),
    ]);
    setFriends(f || []);
    setRequests((r && r.incoming) || []);
    setEvents(e || { upcoming: [], past: [] });
    setPhotos(p || []);
    setFeed((fd && fd.items) || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadSocial(); }, [loadSocial]);

  const openEdit = () => {
    setName(user?.display_name || "");
    setBio(user?.bio || "");
    setPhoto(user?.profile_photo_url || null);
    setEditing(true);
  };

  const choosePhoto = async () => {
    setUploading(true);
    try {
      const res = await pickAndUpload();
      if (res && res.error) toast(res.error);
      else if (res && res[0]) setPhoto(res[0]);
    } catch { toast("Couldn't upload photo"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateProfile({ display_name: name.trim(), profile_photo_url: photo, bio: bio.trim() });
      setUser(updated);
      setEditing(false);
      toast("Profile updated");
    } catch (e) { toast(e.message || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const respond = async (id, accept) => {
    try {
      if (accept) await api.acceptFriend(id); else await api.declineFriend(id);
      await loadSocial();
    } catch (e) { toast(e.message || "Couldn't update"); }
  };

  const logout = async () => { await signOut(); navigate("/login", { replace: true }); };

  return (
    <TabLayout>
      <div className="px-4 pt-6 pb-24" style={{ background: colors.surface }}>
        <div className="flex mb-3"><HomeButton bg={colors.surfaceSecondary} /></div>
        {/* Header */}
        <div className="flex flex-col items-center">
          <button data-testid="profile-edit-photo" onClick={openEdit} className="relative">
            <Avatar name={user?.display_name} uri={user?.profile_photo_url} size={92} />
            <span className="absolute flex items-center justify-center rounded-full" style={{ width: 26, height: 26, bottom: 0, right: 0, background: colors.brand, border: `3px solid ${colors.surface}` }}>
              <Pencil size={12} color={colors.onBrand} />
            </span>
          </button>
          <p className="font-display font-bold text-[22px] mt-3" style={{ color: colors.onSurface }}>{user?.display_name || "You"}</p>
          {user?.bio
            ? <p className="font-display text-[13px] text-center mt-1 max-w-sm" style={{ color: colors.onSurfaceTertiary }}>{user.bio}</p>
            : <button data-testid="add-bio-link" onClick={openEdit} className="font-display text-[12.5px] mt-1" style={{ color: colors.brandSecondary }}>+ Add a bio</button>}
          <div className="flex items-center gap-1.5 mt-1.5">
            <Mail size={12} color={colors.faint} />
            <span className="font-mono2 text-[12px]" style={{ color: colors.faint }}>{user?.email}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center rounded-2xl py-3.5 mt-5" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
          <Stat testid="stat-friends" value={friends.length} label="Friends" />
          <div style={{ width: 1, height: 28, background: colors.border }} />
          <Stat testid="stat-events" value={events.past.length} label="Attended" />
          <div style={{ width: 1, height: 28, background: colors.border }} />
          <Stat testid="stat-photos" value={photos.length} label="Photos" />
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (
          <>
            {/* Friend requests */}
            {requests.length > 0 && (
              <>
                <SectionHeader icon={UserPlus} title={`Friend requests (${requests.length})`} />
                <div className="flex flex-col gap-2">
                  {requests.map((u) => (
                    <div key={u.id} data-testid={`friend-request-${u.id}`} className="flex items-center gap-3 rounded-xl p-2.5" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                      <button onClick={() => navigate(`/user/${u.id}`)} className="shrink-0"><Avatar name={u.display_name} uri={u.profile_photo_url} size={40} /></button>
                      <p className="flex-1 min-w-0 font-display font-medium text-[14px] truncate" style={{ color: colors.onSurface }}>{u.display_name}</p>
                      <button data-testid={`accept-${u.id}`} onClick={() => respond(u.id, true)} className="rounded-full p-2" style={{ background: colors.brand }}><UserCheck size={15} color={colors.onBrand} /></button>
                      <button data-testid={`decline-${u.id}`} onClick={() => respond(u.id, false)} className="rounded-full p-2" style={{ background: colors.surfaceTertiary }}><UserX size={15} color={colors.onSurfaceTertiary} /></button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Friends */}
            <SectionHeader icon={Users} title="Friends" />
            {friends.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
                {friends.map((u) => (
                  <button key={u.id} data-testid={`friend-${u.id}`} onClick={() => navigate(`/user/${u.id}`)} className="flex flex-col items-center gap-1 shrink-0" style={{ width: 62 }}>
                    <Avatar name={u.display_name} uri={u.profile_photo_url} size={52} />
                    <span className="font-display text-[11px] text-center truncate w-full" style={{ color: colors.onSurfaceTertiary }}>{(u.display_name || "").split(" ")[0]}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="font-display text-[12.5px]" style={{ color: colors.faint }}>No friends yet — open a groupmate's profile and tap Add friend.</p>
            )}

            {/* Upcoming */}
            {events.upcoming.length > 0 && (
              <>
                <SectionHeader icon={CalendarHeart} title="Upcoming" />
                <div className="flex flex-col gap-2">
                  {events.upcoming.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={() => navigate(`/event/${ev.id}`)} />)}
                </div>
              </>
            )}

            {/* Memories — past events */}
            <SectionHeader icon={CalendarHeart} title="Events attended" />
            {events.past.length > 0 ? (
              <div className="flex flex-col gap-2">
                {events.past.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={() => navigate(`/event/${ev.id}`)} />)}
              </div>
            ) : (
              <p className="font-display text-[12.5px]" style={{ color: colors.faint }}>Events you RSVP'd Going to will show up here after they happen.</p>
            )}

            {/* Photos */}
            {photos.length > 0 && (
              <>
                <SectionHeader icon={Images} title="Your photos" />
                <div className="grid grid-cols-3 gap-1.5">
                  {photos.slice(0, 18).map((ph) => (
                    <button key={ph.id} data-testid={`my-photo-${ph.id}`} onClick={() => ph.event_id && navigate(`/event/${ph.event_id}`)} className="rounded-lg overflow-hidden" style={{ aspectRatio: "1 / 1", background: colors.surfaceTertiary }}>
                      <img src={mediaUrl(ph.url)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Settings entry */}
            <button data-testid="open-settings" onClick={() => setSettingsOpen(true)} className="flex items-center gap-3 rounded-xl p-4 mt-8 w-full text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <SettingsIcon size={18} color={colors.brandSecondary} />
              <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>Settings</span>
              <ChevronRight size={16} color={colors.muted} />
            </button>

            {user?.is_app_admin && <PlanImport />}
          </>
        )}

        {/* Edit sheet */}
        <Sheet visible={editing} onClose={() => setEditing(false)} title="Edit profile" testID="edit-profile-sheet">
          <button data-testid="edit-photo-picker" onClick={choosePhoto} disabled={uploading} className="relative mx-auto mb-4 flex items-center justify-center rounded-full" style={{ width: 84, height: 84, background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
            {photo ? <img src={mediaUrl(photo)} alt="" className="w-full h-full rounded-full object-cover" /> : <span className="font-display font-bold text-3xl" style={{ color: colors.brandSecondary }}>{(name || "?").slice(0, 1)}</span>}
            <span className="absolute flex items-center justify-center rounded-full" style={{ width: 26, height: 26, bottom: -2, right: -2, background: colors.brand, border: `3px solid ${colors.surfaceSecondary}` }}>
              <Camera size={13} color={colors.onBrand} />
            </span>
          </button>
          <Field label="Your name"><Input testID="edit-name-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Bio"><Input testID="edit-bio-input" placeholder="A short line about you" value={bio} onChange={(e) => setBio(e.target.value.slice(0, 160))} /></Field>
          <PrimaryButton testID="save-profile-button" label="Save changes" onClick={save} disabled={!name.trim()} loading={saving} />
          <GhostButton label="Cancel" color={colors.onSurfaceTertiary} onClick={() => setEditing(false)} />
        </Sheet>

        {/* Settings sheet */}
        <Sheet visible={settingsOpen} onClose={() => setSettingsOpen(false)} title="Settings" testID="settings-sheet">
          <div className="flex flex-col gap-3">
            <button data-testid="edit-profile-button" onClick={() => { setSettingsOpen(false); openEdit(); }} className="flex items-center gap-3 rounded-xl p-4 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <Pencil size={18} color={colors.brandSecondary} />
              <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>Edit profile</span>
              <ChevronRight size={16} color={colors.muted} />
            </button>
            <PushToggle />
            <GoogleAccounts />
            <PartifulConnect />
            <div>
              <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-1" style={{ color: colors.muted }}>Location & privacy</p>
              <ProximitySettings />
            </div>
            <button data-testid="about-link" onClick={() => navigate("/about")} className="flex items-center gap-3 rounded-xl p-4 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <Info size={18} color={colors.brandSecondary} />
              <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>About peeps</span>
              <ChevronRight size={16} color={colors.muted} />
            </button>
            <button data-testid="feedback-link" onClick={() => { setSettingsOpen(false); navigate("/feedback"); }} className="flex items-center gap-3 rounded-xl p-4 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <MessageSquare size={18} color={colors.brandSecondary} />
              <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>Send feedback</span>
              <ChevronRight size={16} color={colors.muted} />
            </button>
            {user?.is_app_admin && (
              <button data-testid="admin-users-link" onClick={() => navigate("/admin/users")} className="flex items-center gap-3 rounded-xl p-4 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                <ShieldCheck size={18} color={colors.brandSecondary} />
                <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>Manage users</span>
                <ChevronRight size={16} color={colors.muted} />
              </button>
            )}
            {user?.is_app_admin && (
              <button data-testid="admin-landing-link" onClick={() => navigate("/login?admin=1")} className="flex items-center gap-3 rounded-xl p-4 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                <ShieldCheck size={18} color={colors.brandSecondary} />
                <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>Edit landing page</span>
                <ChevronRight size={16} color={colors.muted} />
              </button>
            )}
            <button data-testid="logout-button" onClick={logout} className="flex items-center gap-3 rounded-xl p-4 text-left" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <LogOut size={18} color={colors.error} />
              <span className="flex-1 font-display font-medium text-[15px]" style={{ color: colors.error }}>Log out</span>
            </button>
          </div>
        </Sheet>
      </div>
    </TabLayout>
  );
}
